import {
  ApplicationRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import {
  ActiveDirectoryConfiguration,
  FormatType,
  getFormatTypeLabel,
  SubstationTemplateService,
  Translation,
} from 'shared-lib';
import { ToastrService } from 'ngx-toastr';
import { CountryList } from '../../../../models/country.model';
import { CurrencyList } from '../../../../models/currency.model';
import { LanguageList } from '../../../../models/language.model';
import {
  CodedAssetType,
  CodePattern,
  CodePatternOptions,
  DashboardConfiguration,
  MapConfiguration,
  UtilityDetails,
  ValidationRule,
  ConfigurationGeneration,
  getConfigurationGenerationPropertyTypeLabel,
  ConfigurationGenerationPropertyType,
} from 'shared-lib';
import { UtilityService } from '../../../../services/utility.service';
import { UtilitySharedService } from '../../../../shared/_services/utility-shared.service';
import { BlobStorageService } from '../../../../shared/_services/blob-storage.service';
import { ContainerNames } from '../../../../models/container-names.model';

interface Tab {
  key: string;
  label: string;
  position: number;
}

interface ScopedValidationRule extends ValidationRule {
  scopeType?: string | null;
  scopeId?: number | null;
  scopeLabel?: string | null;
}

interface SpecialValidationTypeConfig {
  scopeType: string;
  fieldLabel: string;
  placeholder: string;
  required: boolean;
  loaderKey: string;
  optionIdKey: string;
  optionLabelKey: string;
}

interface ScopeOption {
  id: number;
  label: string;
}

@Component({
  selector: 'app-add-utility',
  standalone: false,
  templateUrl: './add-utility.component.html',
  styleUrl: './add-utility.component.scss',
})
export class AddUtilityComponent implements OnInit {
  @ViewChild('closeModal') closeModal: ElementRef;
  @ViewChild('utilityImage') utilityImageInput: ElementRef;
  @ViewChild('closeCodePatternModal') closeCodePatternModal: ElementRef;
  @ViewChild('closeValidationRuleModal') closeValidationRuleModal: ElementRef;
  @ViewChild('closeConfigurationGenerationModal')
  closeConfigurationGenerationModal: ElementRef;
  serviceRequestImageUrl: string | null;
  @ViewChild('serviceImageInput') serviceImageInput: ElementRef;
  serviceImage: File | null;
  id: number | null = null;
  editUtilityName: string = '';
  utility: UtilityDetails = new UtilityDetails();
  translationKeys: Translation[] = [];
  showUploadModal: boolean = false;
  hasImage: boolean = false;
  hasServiceImage: boolean = false;
  imageSrc: string | ArrayBuffer;
  utilityImage: File | null;
  currencyList = CurrencyList;
  countryList = CountryList;
  languageList = LanguageList;
  utilityList: { key: number; value: string }[] = [];
  selectedUtilityId: number | null = null;

  selectedCodePattern: CodePattern = new CodePattern();
  selectedCodePatternOption?: CodePatternOptions;
  selectedValidationRule: ScopedValidationRule = new ValidationRule();
  selectedConfigurationGeneration: ConfigurationGeneration =
    new ConfigurationGeneration();
  ConfigurationGenerationPropertyType = ConfigurationGenerationPropertyType;
  FormatType = FormatType;

  validationRuleTypeOptions: { value: number; label: string }[] = [
    { value: 1, label: 'AccountNumber' },
    { value: 3, label: 'BuildingSlrn' },
    { value: 4, label: 'MeterNumber' },
    { value: 2, label: 'MeterSLRN' },
    { value: 6, label: 'PoleSlrn' },
    { value: 5, label: 'TransformerSlrn' },
    { value: 7, label: 'IdCardNumber' },
    { value: 8, label: 'RmuSlrn' },
    { value: 9, label: 'MvPoleSlrn' },
    { value: 10, label: 'DtNumber' },
    { value: 11, label: 'SubstationTemplate' },
  ];
  specialTypeMap: Record<number, SpecialValidationTypeConfig> = {
    11: {
      scopeType: 'SubstationTemplate',
      fieldLabel: 'Substations',
      placeholder: 'Select substation',
      required: true,
      loaderKey: 'substationTemplate',
      optionIdKey: 'id',
      optionLabelKey: 'className',
    },
  };
  optionsByLoaderKey: Record<string, ScopeOption[]> = {};
  private originalValidationRulePropertyType: number | null = null;
  private originalValidationRuleScopeId: number | null = null;
  private originalValidationRuleIndex: number | null = null;
  availableValidationRuleTypeOptions: { value: number; label: string }[] = [];
  isEditingValidationRule = false;
  showPrefix: boolean = false;
  mapTypes = [
    { id: 1, name: 'Satellite', value: 'hybrid' },
    { id: 2, name: 'Map', value: 'roadmap' },
  ];
  selectedMapTypeId: number = 1;

  mapThemes = [
    { id: 1, name: 'Standard' },
    { id: 2, name: 'Retro' },
    { id: 3, name: 'Silver' },
    { id: 4, name: 'Night' },
    { id: 5, name: 'Aubergine' },
  ];
  selectedMapThemeId: number = 2;
  selectedAiMapThemeId: number = 2;

  defaultCodePatterns: CodePattern[] = [
    { assetType: CodedAssetType.TransmissionStation, prefix: '' },
    { assetType: CodedAssetType.Feeder33, prefix: '' },
    { assetType: CodedAssetType.Substation, prefix: '' },
    { assetType: CodedAssetType.Feeder11, prefix: '' },
    { assetType: CodedAssetType.Dt, prefix: '' },
    { assetType: CodedAssetType.Pole, prefix: '' },
    { assetType: CodedAssetType.Building, prefix: '' },
    { assetType: CodedAssetType.Customer, prefix: '' },
  ];

  cinAssetTypes: string[] = [
    'Disco',
    'Sub-Disco',
    'Underground',
    'Type',
    '33kV/11kV Trans Substation',
    'Power Transformer',
    '11kV Feeder',
    '11kV Distribution Poles',
    '11kV/400V Transformer',
    'Upriser',
    'Service (LT) Pole',
    'Service Wire',
    'Customer',
  ];

  codePatternOptions = [
    {
      key: CodePatternOptions.TripleNumeric,
      value: 'Numeric 100 (Default)',
    },
    {
      key: CodePatternOptions.SingleNumeric,
      value: 'Single Numeric',
    },
    {
      key: CodePatternOptions.DoubleNumeric,
      value: 'Double Numeric',
    },
    {
      key: CodePatternOptions.QuadrupleNumeric,
      value: 'Numeric 1000',
    },
    {
      key: CodePatternOptions.SingleAlphabet,
      value: 'Single Alphabet',
    },
    {
      key: CodePatternOptions.DoubleAlphabet,
      value: 'Double Alphabet',
    },
    {
      key: CodePatternOptions.TripleAlphabet,
      value: 'Triple Alphabet',
    },
    {
      key: CodePatternOptions.AlphaNumericSingle,
      value: 'Alphanumeric Single',
    },
    {
      key: CodePatternOptions.AlphaNumericDouble,
      value: 'Alphanumeric 10',
    },
    {
      key: CodePatternOptions.AlphaNumericTriple,
      value: 'Alphanumeric 100',
    },
    {
      key: CodePatternOptions.AlphaNumericQuadruple,
      value: 'Alphanumeric 1000',
    },
  ];

  assetTypeOptions = [
    {
      key: CodedAssetType.TransmissionStation,
      name: 'Transmission Station',
    },
    {
      key: CodedAssetType.Feeder33,
      name: '33KV Feeder',
    },
    {
      key: CodedAssetType.Substation,
      name: 'Injection Substation',
    },
    {
      key: CodedAssetType.Feeder11,
      name: '11KV Feeder',
    },
    {
      key: CodedAssetType.Dt,
      name: 'Distribution Substation',
    },
    {
      key: CodedAssetType.Pole,
      name: 'LV Pole',
    },
    {
      key: CodedAssetType.Building,
      name: 'Building',
    },
    {
      key: CodedAssetType.Customer,
      name: 'Customer',
    },
  ];

  circleColors: string[] = [
    '#EF4444',
    '#F97316',
    '#FACC15',
    '#4ADE80',
    '#2DD4BF',
    '#3B82F6',
    '#6366F1',
    '#EC4899',
    '#F43F5E',
    '#D946EF',
    '#8B5CF6',
    '#0EA5E9',
    '#10B981',
    '#84CC16',
    '#160CC9',
    '#01552A',
  ];

  public Configuration = DashboardConfiguration;

  fields = [
    { name: 'Assets', enabled: true, isExpandable: true },
    { name: 'Operations', enabled: false, isExpandable: false },
    { name: 'Meter Management', enabled: false, isExpandable: false },
    { name: 'Readings', enabled: false, isExpandable: false },
    { name: 'Map Configuration', enabled: false, isExpandable: false },
    {
      name: 'Electrical Address Configuration',
      enabled: false,
      isExpandable: false,
    },
  ];

  tabs: Tab[] = [
    {
      key: 'utility-info',
      label: 'UTILITY_MANAGEMENT.UTILITY_INFORMATION',
      position: 0,
    },
    {
      key: 'modal-configuration',
      label: 'UTILITY_MANAGEMENT.SETTINGS_MODAL_CONFIG',
      position: 1,
    },
    { key: 'translator', label: 'UTILITY_MANAGEMENT.TRANSLATOR', position: 2 },
    {
      key: 'configuration-generation',
      label: 'UTILITY_MANAGEMENT.CONFIGURATION_GENERATION.TITLE',
      position: 3,
    },
    {
      key: 'validation-rules',
      label: 'UTILITY_MANAGEMENT.VALIDATION_RULES.TITLE',
      position: 4,
    },
    {
      key: 'electrical-address',
      label: 'UTILITY_MANAGEMENT.ELECTRICAL_ADDRESS_CONFIG.TITLE',
      position: 5,
    },
    {
      key: 'contact-person',
      label: 'UTILITY_MANAGEMENT.CONTACT_PERSON',
      position: 6,
    },
    {
      key: 'new-connection',
      label: 'UTILITY_MANAGEMENT.NEW_CONN_CONSENT',
      position: 7,
    },
    {
      key: 'bank-account',
      label: 'UTILITY_MANAGEMENT.BANK_ACC_INFO',
      position: 8,
    },
    {
      key: 'active-directory',
      label: 'UTILITY_MANAGEMENT.ACTIVE_DIRECTORY.NAME',
      position: 9,
    },
    {
      key: 'email-sender',
      label: 'UTILITY_MANAGEMENT.EMAIL_SENDER.NAME',
      position: 10,
    }
  ];

  configurationGenerationTypeOptions: { value: number; label: string }[] = [
    { value: 1, label: 'AccountNumber' },
    { value: 2, label: 'Spn' },
  ];

  selectedTab = this.tabs[0];
  submitted = false;
  isAddselectedConfigurationGenerationEnabled: boolean = true;

  isSelectedAreasForMobileLimited: boolean = false;
  maximumSelectedAreasForMobile: number | null = 1;

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private appRef: ApplicationRef,
    private utilityService: UtilityService,
    private route: ActivatedRoute,
    private utilitySharedService: UtilitySharedService,
    private substationTemplateService: SubstationTemplateService,
    private blobStorageService: BlobStorageService,
  ) {}

  ngOnInit(): void {
    this.getUtilityKeys();
    this.getUtilities();
    this.blobStorageService.setContainer(ContainerNames.KYC);
    this.id = Number(this.utilitySharedService.getUtilityId());

    if (this.id != null && this.id != 0) {
      this.get(this.id);
    } else {
      if (this.utility.id === 0) {
        this.utility.codePatterns = this.defaultCodePatterns.map((item) => ({
          ...item,
          displayPatternOption: this.presentCodePatternOption(
            item.codePatternOption,
          ),
          displayAssetType: this.presentAssetTypesFromEnumValue(item.assetType),
        }));
      }
    }
  }

  getConfigurationGenerationPropertyTypeLabel =
    getConfigurationGenerationPropertyTypeLabel;
  getFormatTypeLabel = getFormatTypeLabel;

  selectTab(tab: Tab) {
    this.selectedTab = tab;
  }

  openNewValidationRule() {
    this.isEditingValidationRule = false;
    this.originalValidationRulePropertyType = null;
    this.originalValidationRuleScopeId = null;
    this.originalValidationRuleIndex = null;
    this.selectedValidationRule = this.createEmptyValidationRule();
    this.updateAvailableValidationRuleTypes(false);
  }

  openselectedConfigurationGeneration() {
    this.selectedConfigurationGeneration = new ConfigurationGeneration();
  }

  selectConfigurationGeneration(configurationGeneration: any) {
    this.selectedConfigurationGeneration = configurationGeneration;
  }

  saveConfigurationGeneration() {
    if (!this.validateConfigurationGeneration()) return;

    if (this.utility.configurationGenerations == undefined)
      this.utility.configurationGenerations = [];
    const index = this.utility.configurationGenerations.findIndex(
      (element) =>
        element.propertyType ===
        this.selectedConfigurationGeneration.propertyType,
    );
    if (index >= 0) {
      this.utility.configurationGenerations[index] = {
        ...this.selectedConfigurationGeneration,
      };
    } else {
      this.utility.configurationGenerations.push({
        ...this.selectedConfigurationGeneration,
      });
    }

    if (this.utility.configurationGenerations.length >= 2) {
      this.isAddselectedConfigurationGenerationEnabled = false;
    }

    this.closeConfigurationGenerationModal?.nativeElement?.click();
  }

  validateConfigurationGeneration() {
    const normalizedPropertyType =
      this.normalizeConfigurationGenerationPropertyType(
        this.selectedConfigurationGeneration.propertyType,
      );
    this.selectedConfigurationGeneration.propertyType = normalizedPropertyType;
    if (typeof normalizedPropertyType !== 'number') {
      this.toastr.error('Please select property type.');
      return false;
    }

    if (!this.selectedConfigurationGeneration.formatType) {
      this.toastr.error('Please select format type.');
      return false;
    }

    if (
      !this.selectedConfigurationGeneration.length ||
      this.selectedConfigurationGeneration.length <= 0
    ) {
      this.toastr.error('Please enter a valid character length.');
      return false;
    }

    if (
      this.utility.configurationGenerations.some(
        (cg) =>
          cg.propertyType ===
            this.selectedConfigurationGeneration.propertyType &&
          cg !== this.selectedConfigurationGeneration,
      )
    ) {
      this.toastr.error(
        'A configuration generation with the same property type already exists.',
      );
      return false;
    }

    if (
      this.selectedConfigurationGeneration.startsWith.length >=
      this.selectedConfigurationGeneration.length
    ) {
      this.toastr.error(
        'The "Starts With" length must be less than the total length.',
      );
      return false;
    }
    // 1. Slucaj -> Numeric
    if (
      this.selectedConfigurationGeneration.formatType === +FormatType.Numeric
    ) {
      // Treba osigurati da startsWith sadrzi samo brojeve
      if (
        !this.isOnlyNumbers(this.selectedConfigurationGeneration.startsWith)
      ) {
        this.toastr.error(
          'For Numeric format type, "Starts With" can contain only numbers.',
        );
        return false;
      }
      // Treba osigurati da je moguce izvalidirati bar milion kombinacija
    }

    return true;
  }

  private isOnlyNumbers(str: string): boolean {
    return /^[0-9]*$/.test(str);
  }

  private normalizeConfigurationGenerationPropertyType(
    propertyType: number | string | null,
  ) {
    if (typeof propertyType === 'number') {
      return propertyType;
    }

    if (!propertyType) {
      return null;
    }

    const numericValue = Number(propertyType);
    if (!Number.isNaN(numericValue) && numericValue > 0) {
      return numericValue;
    }

    const normalized = propertyType.toString().trim().toLowerCase();
    const option = this.configurationGenerationTypeOptions.find(
      (type) => type.label.toLowerCase() === normalized,
    );

    return option?.value ?? null;
  }

  resetConfigurationGeneration() {
    this.selectedConfigurationGeneration = new ConfigurationGeneration();
  }

  selectValidationRule(rule: ValidationRule, index: number) {
    const normalizedPropertyType = this.normalizeValidationRulePropertyType(
      rule.propertyType,
    );
    const normalizedScopeId = this.normalizeScopeId(
      (rule as ScopedValidationRule).scopeId,
    );
    const specialConfig = this.getSpecialConfig(normalizedPropertyType);

    this.isEditingValidationRule = true;
    this.originalValidationRuleIndex = index;
    this.originalValidationRulePropertyType = normalizedPropertyType;
    this.originalValidationRuleScopeId = normalizedScopeId;
    this.selectedValidationRule = {
      ...rule,
      propertyType: normalizedPropertyType,
      scopeId: normalizedScopeId,
      scopeType: specialConfig?.scopeType ?? null,
    };
    this.ensureOptionsLoaded(normalizedPropertyType);
    this.updateAvailableValidationRuleTypes(true);
  }

  saveValidationRule() {
    if (!this.utility.validationRules) {
      this.utility.validationRules = [];
    }
    const normalizedPropertyType = this.normalizeValidationRulePropertyType(
      this.selectedValidationRule.propertyType,
    );
    this.selectedValidationRule.propertyType = normalizedPropertyType;
    if (typeof normalizedPropertyType !== 'number') {
      this.toastr.error('Please select property type');
      return;
    }

    const specialConfig = this.getSpecialConfig(normalizedPropertyType);
    let selectedScopeId: number | null = null;

    if (specialConfig) {
      selectedScopeId = this.normalizeScopeId(
        this.selectedValidationRule.scopeId,
      );
      if (selectedScopeId == null) {
        this.toastr.error(`${specialConfig.fieldLabel} is required.`);
        return;
      }

      const selectedScopeOption = this.activeScopeOptions.find(
        (option) => option.id === selectedScopeId,
      );
      this.selectedValidationRule.scopeType = specialConfig.scopeType;
      this.selectedValidationRule.scopeId = selectedScopeId;
      this.selectedValidationRule.scopeLabel =
        selectedScopeOption?.label ?? null;
    } else {
      this.clearScopeFields(this.selectedValidationRule);
    }

    const isSameRuleKey = (
      item: ValidationRule,
      propertyType: number,
      scopeId: number | null,
    ): boolean => {
      if (item.propertyType !== propertyType) {
        return false;
      }

      const itemSpecialConfig = this.getSpecialConfig(propertyType);
      if (!itemSpecialConfig) {
        return true;
      }

      const itemScopeId = this.normalizeScopeId(
        (item as ScopedValidationRule).scopeId,
      );
      return itemScopeId === scopeId;
    };

    let index = -1;
    if (
      this.isEditingValidationRule &&
      this.originalValidationRuleIndex != null &&
      this.originalValidationRuleIndex >= 0 &&
      this.originalValidationRuleIndex < this.utility.validationRules.length
    ) {
      index = this.originalValidationRuleIndex;
    } else if (
      this.isEditingValidationRule &&
      this.originalValidationRulePropertyType != null
    ) {
      index = this.utility.validationRules.findIndex((item) =>
        isSameRuleKey(
          item,
          this.originalValidationRulePropertyType!,
          this.originalValidationRuleScopeId,
        ),
      );
    }

    if (index < 0) {
      index = this.utility.validationRules.findIndex((item) =>
        isSameRuleKey(item, normalizedPropertyType, selectedScopeId),
      );
    }

    const duplicateIndex = this.utility.validationRules.findIndex(
      (item, itemIndex) =>
        itemIndex !== index &&
        isSameRuleKey(item, normalizedPropertyType, selectedScopeId),
    );

    if (duplicateIndex >= 0) {
      this.toastr.error(
        'Validation rule with the same property type and scope already exists.',
      );
      return;
    }

    if (index >= 0) {
      this.utility.validationRules[index] = { ...this.selectedValidationRule };
    } else {
      this.utility.validationRules.push({ ...this.selectedValidationRule });
    }
    this.closeValidationRuleModal?.nativeElement?.click();
  }

  resetValidationRuleModal() {
    this.isEditingValidationRule = false;
    this.originalValidationRulePropertyType = null;
    this.originalValidationRuleScopeId = null;
    this.originalValidationRuleIndex = null;
    this.selectedValidationRule = this.createEmptyValidationRule();
    this.updateAvailableValidationRuleTypes(false);
  }

  updateAvailableValidationRuleTypes(includeSelected: boolean) {
    const usedTypes = new Set(
      this.utility.validationRules
        .map((rule) => rule.propertyType)
        .filter((type): type is number => typeof type === 'number'),
    );

    if (
      includeSelected &&
      typeof this.selectedValidationRule.propertyType === 'number'
    ) {
      usedTypes.delete(this.selectedValidationRule.propertyType);
    }

    this.availableValidationRuleTypeOptions =
      this.validationRuleTypeOptions.filter(
        (type) => this.isSpecialType(type.value) || !usedTypes.has(type.value),
      );
  }

  onValidationRuleTypeChange(value: number | string | null) {
    const normalizedPropertyType =
      this.normalizeValidationRulePropertyType(value);
    this.selectedValidationRule.propertyType = normalizedPropertyType;

    const config = this.getSpecialConfig(normalizedPropertyType);
    if (!config) {
      this.clearScopeFields(this.selectedValidationRule);
      return;
    }

    this.selectedValidationRule.scopeType = config.scopeType;
    this.ensureOptionsLoaded(normalizedPropertyType);
  }

  onConfigurationGenerationFormatTypeChange(value: number) {
    this.selectedConfigurationGeneration.formatType = value as FormatType;
  }

  onConfigurationGenerationPropertyTypeChange(value: number) {
    this.selectedConfigurationGeneration.propertyType =
      value as ConfigurationGenerationPropertyType;
  }

  getValidationRuleLabel(propertyType: number | null) {
    if (propertyType == null) {
      return '';
    }

    return (
      this.validationRuleTypeOptions.find((type) => type.value === propertyType)
        ?.label ?? ''
    );
  }

  getValidationRuleScopeLabel(rule: ValidationRule): string {
    if (!this.isSpecialType(rule.propertyType)) {
      return '-';
    }

    const scopedRule = rule as ScopedValidationRule;
    if (scopedRule.scopeLabel) {
      return scopedRule.scopeLabel;
    }

    const scopeId = this.normalizeScopeId(scopedRule.scopeId);
    if (scopeId == null) {
      return 'NA';
    }

    return (
      this.getScopeOptionsForPropertyType(rule.propertyType).find(
        (option) => option.id === scopeId,
      )?.label ?? 'NA'
    );
  }

  private normalizeValidationRulePropertyType(
    propertyType: number | string | null | { value?: unknown },
  ) {
    if (typeof propertyType === 'number') {
      return propertyType;
    }

    if (!propertyType) {
      return null;
    }

    if (
      typeof propertyType === 'object' &&
      typeof propertyType.value === 'number'
    ) {
      return propertyType.value;
    }

    const numericValue = Number(propertyType);
    if (!Number.isNaN(numericValue) && numericValue > 0) {
      return numericValue;
    }

    const normalized = propertyType.toString().trim().toLowerCase();
    const option = this.validationRuleTypeOptions.find(
      (type) => type.label.toLowerCase() === normalized,
    );

    return option?.value ?? null;
  }

  private loadOptionsForLoaderKey(loaderKey: string) {
    if (this.optionsByLoaderKey[loaderKey] !== undefined) {
      return;
    }

    if (loaderKey === 'substationTemplate') {
      if (this.id == null || this.id < 0) {
        this.optionsByLoaderKey[loaderKey] = [];
        return;
      }

      this.substationTemplateService
        .getAllForSelectByUtility(this.id)
        .subscribe({
          next: (response: any) => {
            const responseData = response?.body;
            const templates = responseData?.data ?? [];
            this.optionsByLoaderKey[loaderKey] = templates
              .filter((template: any) => typeof template?.id === 'number')
              .map((template: any) => ({
                id: template.id,
                label: template.className,
              }));
            this.hydrateScopeLabelsForLoaderKey(loaderKey);
            this.appRef.tick();
          },
          error: (_) => {
            this.optionsByLoaderKey[loaderKey] = [];
            this.toastr.error(
              'Error occurred while loading substation templates.',
            );
          },
        });
      return;
    }

    this.optionsByLoaderKey[loaderKey] = [];
  }

  private getSpecialConfig(
    propertyType: number | null | undefined,
  ): SpecialValidationTypeConfig | null {
    if (propertyType == null) {
      return null;
    }

    return this.specialTypeMap[propertyType] ?? null;
  }

  private isSpecialType(propertyType: number | null | undefined): boolean {
    return this.getSpecialConfig(propertyType) != null;
  }

  private ensureOptionsLoaded(propertyType: number | null | undefined) {
    const config = this.getSpecialConfig(propertyType);
    if (!config) {
      return;
    }

    this.loadOptionsForLoaderKey(config.loaderKey);
  }

  private preloadScopeOptionsForExistingRules() {
    if (!this.utility.validationRules?.length) {
      return;
    }

    const propertyTypesToLoad = Array.from(
      new Set(
        this.utility.validationRules
          .map((rule) =>
            this.normalizeValidationRulePropertyType(rule.propertyType),
          )
          .filter((propertyType): propertyType is number =>
            this.isSpecialType(propertyType),
          ),
      ),
    );

    propertyTypesToLoad.forEach((propertyType) =>
      this.ensureOptionsLoaded(propertyType),
    );
  }

  private hydrateScopeLabelsForLoaderKey(loaderKey: string) {
    const options = this.optionsByLoaderKey[loaderKey] ?? [];
    if (!options.length || !this.utility.validationRules?.length) {
      return;
    }

    this.utility.validationRules = this.utility.validationRules.map((rule) => {
      const propertyType = this.normalizeValidationRulePropertyType(
        rule.propertyType,
      );
      const config = this.getSpecialConfig(propertyType);

      if (!config || config.loaderKey !== loaderKey) {
        return rule;
      }

      const scopeId = this.normalizeScopeId(
        (rule as ScopedValidationRule).scopeId,
      );
      const scopeLabel =
        options.find((option) => option.id === scopeId)?.label ?? null;

      return {
        ...rule,
        scopeLabel,
      } as ScopedValidationRule;
    });
  }

  private clearScopeFields(rule: ScopedValidationRule) {
    rule.scopeType = null;
    rule.scopeId = null;
    rule.scopeLabel = null;
  }

  private normalizeScopeId(value: unknown): number | null {
    if (typeof value === 'number' && value >= 0) {
      return value;
    }

    if (typeof value === 'string') {
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue) && numericValue >= 0) {
        return numericValue;
      }
    }

    return null;
  }

  private createEmptyValidationRule(): ScopedValidationRule {
    return new ValidationRule();
  }

  getScopeOptionsForPropertyType(
    propertyType: number | null | undefined,
  ): ScopeOption[] {
    const config = this.getSpecialConfig(propertyType);
    if (!config) {
      return [];
    }

    return this.optionsByLoaderKey[config.loaderKey] ?? [];
  }

  get activeSpecialConfig(): SpecialValidationTypeConfig | null {
    return this.getSpecialConfig(this.selectedValidationRule.propertyType);
  }

  get activeScopeOptions(): ScopeOption[] {
    return this.getScopeOptionsForPropertyType(
      this.selectedValidationRule.propertyType,
    );
  }
  continue(tabNum: number) {
    this.selectedTab = this.tabs[tabNum];
  }

  save() {
    if (!this.validateForm()) {
      return;
    }

    return;
  }

  validateForm() {
    const invalidKeys = this.getInvalidSectionKeys();
    if (invalidKeys.length > 0) {
      const names = invalidKeys
        .map((k) => this.getSectionDisplayName(k))
        .filter((x) => !!x);
      const uniqueNames = Array.from(new Set(names));
      const firstInvalidKey = invalidKeys[0];
      const firstTab = this.tabs.find((t) => t.key === firstInvalidKey);
      if (firstTab) {
        this.selectedTab = firstTab;
      }
      this.toastr.error(
        `Some mandatory fields in the ${uniqueNames.join(', ')} sections have not been filled.`,
      );
      return false;
    }
    return true;
  }

  onCodePatternsToggle(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.utility.defaultCaimsCodePatterns = isChecked;
  }

  onCinPatternsToggle(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.utility.defaultCaimsCodePatterns = !isChecked;
  }

  validateModalConfiguration() {
    if (this.utility.dashboardConfiguration.length == 0) {
      return false;
    }
    return true;
  }

  validateBankAccInformation() {
    if (
      !this.utility.bankDetails.bankName ||
      !this.utility.bankDetails.accountName ||
      !this.utility.bankDetails.accountNumber
    ) {
      return false;
    }

    return true;
  }

  validateContactPerson() {
    if (
      !this.utility.contactPerson.lastName ||
      !this.utility.contactPerson.firstName ||
      !this.utility.contactPerson.eMail
    ) {
      return false;
    }

    return true;
  }

  validateUtilityInfo() {
    if (
      !this.utility.name ||
      !this.utility.acronym ||
      !this.hasImage ||
      !this.utility.colorCode ||
      !this.utility.country ||
      !this.utility.language ||
      !this.utility.currency
    ) {
      return false;
    }

    return true;
  }

  validateActiveDirectory() {
    if (
      this.utility.activeDirectoryConfiguration?.isEnabled &&
      (!this.utility.activeDirectoryConfiguration.applicationId ||
      !this.utility.activeDirectoryConfiguration.objectId ||
      !this.utility.activeDirectoryConfiguration.tenantId)
    ) {
      return false;
    }

    return true;
  }

   validateEmailSender() {
    if (
      (!(!!this.utility.sendgridApiKey === !!this.utility.senderEmail))
    ) {
      return false;
    }

    return true;
  }

  onBackButtonClicked() {
    this.utilitySharedService.removeUtilityId();
    this.router.navigate(['settings-bps/utility-management']);
  }

  onChange(configuration: any, isChecked: any = null) {
    configuration.forEach((value: any) => {
      if (event != null) {
        if (!this.isChecked([value]) && isChecked == true)
          this.utility.dashboardConfiguration.push(value);
        else if (this.isChecked([value]) && isChecked == false)
          this.utility.dashboardConfiguration =
            this.utility.dashboardConfiguration.filter(
              (item: any) => item != value,
            );
      } else {
        if (this.utility.dashboardConfiguration.includes(value))
          this.utility.dashboardConfiguration =
            this.utility.dashboardConfiguration.filter(
              (item: any) => item != value,
            );
        else this.utility.dashboardConfiguration.push(value);
      }
    });
  }

  isChecked(listConfig: any) {
    let checked = true;
    listConfig.forEach((val: any) => {
      if (!this.utility.dashboardConfiguration.includes(val)) checked = false;
    });
    return checked;
  }

  toggleActiveDirectory() {
    if (!this.utility.activeDirectoryConfiguration)
      this.utility.activeDirectoryConfiguration = new ActiveDirectoryConfiguration();

    this.utility.activeDirectoryConfiguration.isEnabled =
      !this.utility.activeDirectoryConfiguration.isEnabled;
  }

  getUtilityKeys() {
    var jsonTranslations = localStorage.getItem('translation');
    var data = JSON.parse(jsonTranslations!);
    this.translationKeys = data;
  }

  onMapConfigurationSaved(config: MapConfiguration) {
    this.utility.mapConfiguration = config;
  }

  onFileDropped($event: any) {
    this.utilityImage = $event.item(0);
    if (
      this.utilityImage?.type != 'image/png' &&
      this.utilityImage?.type != 'image/jpg' &&
      this.utilityImage?.type != 'image/jpeg'
    ) {
      this.toastr.warning('Only PNG/JPG/JPEG images are allowed');
      if (this.utilityImageInput?.nativeElement) {
        this.utilityImageInput.nativeElement.value = '';
      }
    } else {
      this.hasImage = true;
      const reader = new FileReader();
      var width = 0;
      var height = 0;
      reader.onload = (e) => {
        this.imageSrc = reader.result!;
        const img = new Image();
        img.src = e.target!.result as string;

        img.onload = () => {
          width = img.width;
          height = img.height;
          var ratio = width / height;
          if (ratio > 2 || ratio < 0.5) {
            this.toastr.warning('Unsuported image size.');
            this.imageSrc = '';
            this.hasImage = false;
            this.utilityImage = null;
            this.appRef.tick();
          }
        };
      };
      reader.readAsDataURL(this.utilityImage!);
    }
  }
  triggerFileInput() {
    this.utilityImageInput.nativeElement.click();
  }
  triggerServiceFileInput() {
    this.serviceImageInput.nativeElement.click();
  }

  fileBrowserHandler($event: any) {
    this.utilityImage = $event.target.files.item(0);
    if (
      this.utilityImage?.type != 'image/png' &&
      this.utilityImage?.type != 'image/jpg' &&
      this.utilityImage?.type != 'image/jpeg'
    ) {
      this.toastr.warning('Only PNG/JPG/JPEG images are allowed');
      if (this.utilityImageInput?.nativeElement) {
        this.utilityImageInput.nativeElement.value = '';
      }
    } else {
      this.hasImage = true;
      const reader = new FileReader();
      var width = 0;
      var height = 0;
      reader.onload = (e) => {
        this.imageSrc = reader.result!;
        const img = new Image();
        img.src = e.target!.result as string;

        img.onload = () => {
          width = img.width;
          height = img.height;
          var ratio = width / height;
          if (ratio > 2 || ratio < 0.5) {
            this.toastr.warning('Unsuported image size.');
            this.imageSrc = '';
            this.hasImage = false;
            this.utilityImage = null;
            this.appRef.tick();
          }
        };
      };
      reader.readAsDataURL(this.utilityImage!);
    }
  }

  removeImage(event: MouseEvent) {
    event.stopPropagation();
    this.utility.imageUrl = '';
    this.hasImage = false;
  }

  onColorChange(event: any) {
    this.utility.colorCode = event.color?.hex || event.target.value;
  }

  selectColorCircle(color: string, index: number) {
    this.utility.colorCode = color;
  }

  selectUtilityCopy(e: any) {
    if (e.target.value != '') {
      this.utility.utilityIdCopyFrom = e.target.value;
    }
  }

  selectCountry(e: any) {
    if (e.target.value != '') {
      this.utility.country = e.target.value;
    } else {
      this.utility.country = '';
    }
  }

  selectLanguage(e: any) {
    if (e.target.val != '') {
      this.utility.language = e.target.value;
    } else {
      this.utility.language = '';
    }
  }

  selectCurrency(e: any) {
    if (e.target.value != '') {
      this.utility.currency = e.target.value;
    } else {
      this.utility.currency = undefined;
    }
  }

  getUtilities() {
    this.utilityService.getUtilitiesForSelect().subscribe({
      next: (data) => {
        this.utilityList = data.data;
      },
      error: (error) => console.log(error.statusText),
    });
  }

  saveUtility() {
    this.submitted = true;
    if (!this.validateForm()) {
      return;
    }
    this.utility.translations = this.translationKeys;
    this.utility.isSelectedAreasForMobileLimited = this.isSelectedAreasForMobileLimited;
    this.utility.maximumSelectedAreasForMobile = this.isSelectedAreasForMobileLimited ? this.maximumSelectedAreasForMobile : null;

    this.utilityService.add(this.utility, this.utilityImage).subscribe({
      next: (response) => {
        const responseData = response;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          if (responseData.message === 'Successfully added utility') {
            this.toastr.success(responseData.message);
            this.router.navigate(['settings-bps/utility-management']);
          } else if (responseData.data === 'Successfully updated utility') {
            this.toastr.success(responseData.data);
            this.router.navigate(['settings-bps/utility-management']);
          } else {
            this.toastr.warning(responseData.message);
          }
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (error) => {
        this.toastr.error('Error occured');
      },
    });
  }

  get(id: number) {
    this.utilityService.get(id).subscribe({
      next: (response) => {
        const responseData = response.body;
        if (responseData?.status === 200 || responseData?.status === 'OK') {
          this.utility = responseData.data;
          this.utility.isSelectedAreasForMobileLimited = !this.utility.isSelectedAreasForMobileLimited ? false : this.utility.isSelectedAreasForMobileLimited;
          this.isSelectedAreasForMobileLimited = this.utility.isSelectedAreasForMobileLimited;
          this.maximumSelectedAreasForMobile = this.utility.maximumSelectedAreasForMobile ?? null;
          this.utility.validationRules = (
            this.utility.validationRules || []
          ).map((rule) => ({
            ...rule,
            propertyType: this.normalizeValidationRulePropertyType(
              rule.propertyType,
            ),
          }));
          this.preloadScopeOptionsForExistingRules();
          this.utility.configurationGenerations = (
            responseData.data.configurationGeneration || []
          ).map((item: any) => ConfigurationGeneration.fromData(item));
          if (this.utility.configurationGenerations.length >= 2) {
            this.isAddselectedConfigurationGenerationEnabled = false;
          }
          this.hasImage = true;
          this.selectedMapTypeId =
            this.mapTypes.find(
              (x) => x.value === this.utility.mapConfiguration?.mapType,
            )?.id || 1;
          this.selectedMapThemeId =
            this.mapThemes.find(
              (x) =>
                x.name.toLowerCase() ===
                this.utility.mapConfiguration?.mapTheme,
            )?.id || 2;
          this.selectedAiMapThemeId =
            this.mapThemes.find(
              (x) =>
                x.name.toLowerCase() ===
                this.utility.aiMapConfiguration?.mapTheme,
            )?.id || 2;
          this.editUtilityName = this.utility.name;
          this.serviceRequestImageUrl =
            this.utility.kycConsentConfiguration.serviceRequestImageUrl;
          this.hasServiceImage = this.serviceRequestImageUrl != null;
          const foundLang = this.languageList.find(
            (x) => x.code == this.utility.language,
          );
          this.utility.language = foundLang ? foundLang.text : '';
          this.utility.codePatterns = this.utility.codePatterns.map((item) => ({
            ...item,
            displayPatternOption: this.presentCodePatternOption(
              item.codePatternOption,
            ),
            displayAssetType: this.presentAssetTypes(item.assetType),
          }));
          this.resolveEnum();
        } else {
          this.toastr.error(responseData?.message);
        }
      },
      error: (_) => {
        this.toastr.error('Error occured');
      },
    });
  }

  clearCodePattern() {
    this.selectedCodePatternOption = undefined;
    this.selectedCodePattern = new CodePattern();
  }

  presentAssetTypes(type?: CodedAssetType) {
    if (type) {
      const assetType =
        CodedAssetType[type?.toString() as keyof typeof CodedAssetType];
      var res = this.assetTypeOptions.find((x) => x.key == assetType)?.name;
      return res;
    }
    return '';
  }

  presentAssetTypesFromEnumValue(type?: CodedAssetType): string {
    if (type !== undefined && type !== null) {
      const res = this.assetTypeOptions.find((x) => x.key === type)?.name;
      return res ?? '';
    }
    return '';
  }
  resolveEnum() {
    var enums: any[] = [];
    var enumm: DashboardConfiguration;
    this.utility.dashboardConfiguration.forEach((obj) => {
      enumm = obj as DashboardConfiguration;
      enums.push(DashboardConfiguration[enumm]);
    });
    this.utility.dashboardConfiguration = enums;
  }

  selectPattern(e: any) {
    if (e.target.value != '') {
      this.selectedCodePattern.codePatternOption =
        CodePatternOptions[e.target.value as keyof typeof CodePatternOptions];
      this.selectedCodePattern.prefix = '';
      this.checkPrefixVisibility();
    }
  }

  selectCodePattern(item: CodePattern) {
    this.selectedCodePattern = { ...item };
    this.selectedCodePatternOption = this.selectedCodePattern.codePatternOption
      ? CodePatternOptions[
          this.selectedCodePattern.codePatternOption?.toString() as keyof typeof CodePatternOptions
        ]
      : undefined;
    this.checkPrefixVisibility(item.codePatternOption);
  }

  checkPrefixVisibility(item?: CodePatternOptions) {
    const pattern =
      CodePatternOptions[
        item
          ? item
          : (this.selectedCodePattern.codePatternOption?.toString() as keyof typeof CodePatternOptions)
      ];
    if (
      pattern == CodePatternOptions.AlphaNumericSingle ||
      pattern == CodePatternOptions.AlphaNumericDouble ||
      pattern == CodePatternOptions.AlphaNumericTriple ||
      pattern == CodePatternOptions.AlphaNumericQuadruple
    ) {
      this.showPrefix = true;
    } else {
      this.showPrefix = false;
    }
  }

  saveCodePattern() {
    this.selectedCodePattern.displayPatternOption =
      this.presentCodePatternOption(this.selectedCodePattern.codePatternOption);
    var indexToChange = this.utility.codePatterns.findIndex(
      (item) => item.assetType == this.selectedCodePattern.assetType,
    );
    this.utility.codePatterns[indexToChange] = this.selectedCodePattern;
    this.selectedCodePattern = new CodePattern();
    this.closeCodePatternModal?.nativeElement?.click();
  }

  presentCodePatternOption(option?: CodePatternOptions) {
    if (option) {
      const pattern =
        CodePatternOptions[
          option?.toString() as keyof typeof CodePatternOptions
        ];
      var res = this.codePatternOptions.find((x) => x.key == pattern)?.value;
      return res;
    }
    return '';
  }

  selectMapType(event: number) {
    if (this.utility.mapConfiguration) {
      const mapType = this.mapTypes.find((x) => x.id === event);
      this.utility.mapConfiguration.mapType = mapType?.value || '';
      this.selectedMapTypeId = event;
    }
  }

  selectMapTheme(event: number) {
    if (this.utility.mapConfiguration) {
      const mapTheme = this.mapThemes.find((x) => x.id === event);
      this.utility.mapConfiguration.mapTheme =
        mapTheme?.name.toLowerCase() || '';
      this.selectedMapThemeId = event;
    }
  }

  isFormValid(): boolean {
    return (
      this.validateUtilityInfo() &&
      this.validateModalConfiguration() &&
      this.validateContactPerson() &&
      this.validateActiveDirectory()
    );
  }

  hasErrorsForTab(tabKey: string): boolean {
    switch (tabKey) {
      case 'utility-info':
        return !this.validateUtilityInfo();
      case 'modal-configuration':
        return !this.validateModalConfiguration();
      case 'contact-person':
        return !this.validateContactPerson();
      default:
        return false;
    }
  }

  private getInvalidSectionKeys(): string[] {
    const invalid: string[] = [];
    if (!this.validateUtilityInfo()) invalid.push('utility-info');
    if (!this.validateModalConfiguration()) invalid.push('modal-configuration');
    if (!this.validateContactPerson()) invalid.push('contact-person');
    if (!this.validateActiveDirectory()) invalid.push('active-directory');
    if (!this.validateEmailSender()) invalid.push('email-sender');
    return invalid;
  }

  private getSectionDisplayName(key: string): string {
    const map: Record<string, string> = {
      'utility-info': 'Utility Information',
      'modal-configuration': 'Modal Configuration',
      'validation-rules': 'Validation Rules',
      'electrical-address': 'Electrical Address',
      'contact-person': 'Contact Person',
      'new-connection': 'New Connection',
      'bank-account': 'Bank Account',
      'active-directory': 'Active Directory',
      'email-sender': 'Email Sender'
    };
    return map[key] || key;
  }

  delete(event: MouseEvent, config: ConfigurationGeneration) {
    event.stopPropagation();
    event.preventDefault();

    this.utility.configurationGenerations =
      this.utility.configurationGenerations?.filter(
        (x) => x.propertyType !== config.propertyType,
      );
    if (this.utility.configurationGenerations?.length < 2) {
      this.isAddselectedConfigurationGenerationEnabled = true;
    }
  }

  onRowClick(configuration: any): void {
    if (configuration.id === 0) {
      this.selectConfigurationGeneration(configuration);
    }
  }

  onServiceFileDropped($event: any) {
    this.serviceImage = $event.item(0);
    if (
      this.serviceImage?.type != 'image/png' &&
      this.serviceImage?.type != 'image/jpg' &&
      this.serviceImage?.type != 'image/jpeg'
    ) {
      this.toastr.warning('Only PNG/JPG/JPEG images are allowed');
      if (this.serviceImageInput?.nativeElement) {
        this.serviceImageInput.nativeElement.value = '';
      }
    } else {
      const reader = new FileReader();
      const maxSize = 2 * 1024 * 1024;
      if (this.serviceImage && this.serviceImage.size > maxSize) {
        this.toastr.warning('Image must be smaller than 2 MB.');
        this.imageSrc = '';
        this.serviceImage = null;
        this.appRef.tick();
        return;
      }

      this.serviceImageUpload(this.serviceImage);
    }
  }
  serviceFileBrowserHandler($event: any) {
    this.serviceImage = $event.target.files.item(0);
    if (
      this.serviceImage?.type != 'image/png' &&
      this.serviceImage?.type != 'image/jpg' &&
      this.serviceImage?.type != 'image/jpeg'
    ) {
      this.toastr.warning('Only PNG/JPG/JPEG images are allowed');
      if (this.serviceImageInput?.nativeElement) {
        this.serviceImageInput.nativeElement.value = '';
      }
    } else {
      const maxSize = 2 * 1024 * 1024;
      if (this.serviceImage && this.serviceImage.size > maxSize) {
        this.toastr.warning('Image must be smaller than 2 MB.');
        this.imageSrc = '';
        this.serviceImage = null;
        this.appRef.tick();
        return;
      }
      this.serviceImageUpload(this.serviceImage);
    }
  }
  removeServiceImage(event: MouseEvent) {
    event.stopPropagation();
    this.serviceImage = null;
    this.hasServiceImage = false;
    this.serviceRequestImageUrl = '';
  }
  serviceImageUpload(data: File | null) {
    if (data === null) return;

    this.blobStorageService.uploadFile(
      data,
      data.name,
      (response) => {
        this.serviceRequestImageUrl = response;
        this.hasServiceImage = true;
        this.utility.kycConsentConfiguration.serviceRequestImageUrl = response;
      },
      (error) => {
        this.toastr.error('Failed to upload Service request image');
      },
    );
  }

  validateLimit() {
    if (!this.maximumSelectedAreasForMobile || this.maximumSelectedAreasForMobile < 1) {
      this.maximumSelectedAreasForMobile = 1;
    }
  }
}
